import { useEffect, useMemo, useRef, useState } from 'react';
import { dayKey, netPnl, tradingDayKey } from './pnl';
import { useI18n } from './i18n';
import { currentLocale, getCurrentLang, translateNow as tn } from './langState';
import { trackEvent } from './analytics';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
];
export const SIZES = {
  wide: { w: 1200, h: 675, label: 'Wide (X, Discord)' },
  square: { w: 1080, h: 1080, label: 'Square (Instagram)' },
};
export const COLORS = { bg: '#040d1f', panel: '#071d33', border: '#15506a', cyan: '#1fd7ef', text: '#f1f6fb', muted: '#93a7bd', faint: '#5f7891', gain: '#2fe39a', loss: '#ff5d73' };
// The Market Matrix logo, from Brand.js (viewBox 255 294 745 660).
const LOGO_M = 'M263 297L626 531L991 297L996 951H878L879 497L848 520L720 600L613 663L375 503L377 951H258Z';
const LOGO_BARS = ['M399 818L492 772V951H399Z', 'M521 750L614 705V951H521Z', 'M642 684L737 634V951H642Z', 'M765 606L855 556V951H765Z'];

function periodStart(id) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (id === 'week') d.setDate(d.getDate() - d.getDay());
  if (id === 'month') d.setDate(1);
  return d;
}

function money(n) {
  const sign = n > 0 ? '+' : n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Everything the card shows for a period.
export function shareStats(trades, period, account) {
  const start = periodStart(period);
  const list = trades.filter((t) => new Date(t.date) >= start).sort((a, b) => new Date(a.date) - new Date(b.date));
  const nets = list.map(netPnl);
  const net = nets.reduce((a, b) => a + b, 0);
  const byDay = {};
  for (const t of list) byDay[tradingDayKey(t.date)] = (byDay[tradingDayKey(t.date)] || 0) + netPnl(t);
  const dayNets = Object.values(byDay);
  // One bar per trade today, or one slot per day of the whole week or month (days still to come stay empty).
  let bars;
  if (period === 'today') bars = nets;
  else {
    bars = [];
    const slots = period === 'week' ? 7 : new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
    for (let i = 0; i < slots; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      bars.push(byDay[dayKey(d)] || 0);
    }
  }
  const grossWin = nets.filter((n) => n > 0).reduce((a, b) => a + b, 0);
  const grossLoss = -nets.filter((n) => n < 0).reduce((a, b) => a + b, 0);
  return {
    count: list.length,
    net,
    winRate: list.length ? (nets.filter((n) => n > 0).length / list.length) * 100 : null,
    greenDays: dayNets.filter((n) => n > 0).length,
    tradingDays: dayNets.length,
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : null,
    percent: account && account.size ? (net / account.size) * 100 : null,
    bars,
  };
}

function periodTitle(period) {
  const now = new Date();
  if (period === 'today') return `${tn('Today')} · ${now.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })}`;
  if (period === 'month') return now.toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' });
  return tn('Week of {date}', { date: periodStart('week').toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' }) });
}

export function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export function drawLogo(ctx, x, y, size) {
  roundRect(ctx, x, y, size, size, size * 0.22);
  ctx.fillStyle = COLORS.text;
  ctx.fill();
  const scale = (size * 0.74) / 745;
  ctx.save();
  ctx.translate(x + size * 0.13, y + size * 0.14);
  ctx.scale(scale, scale);
  ctx.translate(-255, -294);
  ctx.fillStyle = '#01193e';
  ctx.fill(new Path2D(LOGO_M));
  ctx.fillStyle = '#00dbe7';
  for (const d of LOGO_BARS) ctx.fill(new Path2D(d));
  ctx.restore();
}

// The member's gold badge, right-aligned at (right, y): "★ FOUNDER" for the creator in solid
// gold, "★ FOUNDING MEMBER #12" outlined for founding members.
export function drawBadge(ctx, badge, right, y) {
  const text = badge.creator ? `★ ${tn('FOUNDER')}` : `★ ${tn('FOUNDING MEMBER #{n}', { n: badge.number })}`;
  ctx.font = '800 17px Inter, sans-serif';
  const w = ctx.measureText(text).width + 32;
  const x = right - w;
  roundRect(ctx, x, y, w, 36, 18);
  if (badge.creator) {
    const gold = ctx.createLinearGradient(x, y, x + w, y + 36);
    gold.addColorStop(0, '#f8cf6b');
    gold.addColorStop(1, '#f5b941');
    ctx.fillStyle = gold;
    ctx.fill();
    ctx.fillStyle = '#1a1204';
  } else {
    ctx.fillStyle = 'rgba(245, 185, 65, 0.12)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(245, 185, 65, 0.75)';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = '#f5b941';
  }
  ctx.textAlign = 'center';
  ctx.fillText(text, x + w / 2, y + 24);
  ctx.textAlign = 'left';
}

function drawCard(canvas, { stats, period, hideAmounts, sizeId, name, badge }) {
  const { w, h } = SIZES[sizeId];
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  const square = sizeId === 'square';
  const pad = square ? 80 : 70;

  drawBackdrop(ctx, w, h, pad);
  ctx.textAlign = 'right';
  ctx.fillStyle = COLORS.muted;
  ctx.font = '700 22px Inter, sans-serif';
  ctx.fillText(periodTitle(period).toUpperCase(), w - pad, pad + 32);
  if (name) {
    ctx.fillStyle = COLORS.faint;
    ctx.font = '600 18px Inter, sans-serif';
    ctx.fillText(name, w - pad, pad + 58);
  }
  ctx.textAlign = 'left';
  if (badge) drawBadge(ctx, badge, w - pad, pad + (name ? 76 : 50));

  // Headline: P&L, or % of the account / win rate when amounts are hidden.
  const top = square ? 300 : 240;
  const up = stats.net >= 0;
  let label = 'NET P&L';
  let big = money(stats.net);
  if (hideAmounts) {
    if (stats.percent !== null) { label = 'ACCOUNT RETURN'; big = `${stats.percent >= 0 ? '+' : ''}${stats.percent.toFixed(2)}%`; }
    else { label = 'WIN RATE'; big = stats.winRate === null ? '—' : `${Math.round(stats.winRate)}%`; }
  }
  ctx.fillStyle = COLORS.muted;
  ctx.font = '800 20px Inter, sans-serif';
  ctx.fillText(tn(label), pad, top - 70);
  ctx.fillStyle = label === 'WIN RATE' ? COLORS.text : up ? COLORS.gain : COLORS.loss;
  ctx.font = `600 ${square ? 112 : 96}px "JetBrains Mono", Menlo, monospace`;
  ctx.fillText(big, pad - 4, top + 20);

  // Stat boxes.
  const pf = stats.profitFactor === null ? '—' : stats.profitFactor === Infinity ? '∞' : stats.profitFactor.toFixed(2);
  const boxes = [
    ['TRADES', String(stats.count)],
    ['WIN RATE', stats.winRate === null ? '—' : `${Math.round(stats.winRate)}%`],
    ['GREEN DAYS', `${stats.greenDays}/${stats.tradingDays}`],
    ['PROFIT FACTOR', pf],
  ].filter(([l]) => !(hideAmounts && l === 'WIN RATE' && label === 'WIN RATE'));
  const boxY = top + (square ? 80 : 60);
  const boxW = (w - pad * 2 - 16 * (boxes.length - 1)) / boxes.length;
  boxes.forEach(([l, v], i) => {
    const x = pad + i * (boxW + 16);
    roundRect(ctx, x, boxY, boxW, 104, 16);
    ctx.fillStyle = 'rgba(7, 29, 51, 0.92)';
    ctx.fill();
    ctx.strokeStyle = COLORS.border;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = COLORS.muted;
    ctx.font = '800 15px Inter, sans-serif';
    ctx.fillText(tn(l), x + 22, boxY + 36);
    ctx.fillStyle = COLORS.text;
    ctx.font = '600 36px "JetBrains Mono", Menlo, monospace';
    ctx.fillText(v, x + 20, boxY + 82);
  });

  // Daily (or per-trade) bars.
  const chartY = boxY + 104 + (square ? 70 : 40);
  const chartH = h - chartY - (square ? 150 : 110);
  if (chartH > 40 && stats.bars.length) {
    ctx.fillStyle = COLORS.faint;
    ctx.font = '800 14px Inter, sans-serif';
    ctx.fillText(tn(period === 'today' ? 'EACH TRADE' : 'EACH DAY'), pad, chartY - 12);
    const maxAbs = Math.max(1, ...stats.bars.map(Math.abs));
    const hasNeg = stats.bars.some((b) => b < 0);
    const hasPos = stats.bars.some((b) => b > 0);
    const zero = chartY + (hasNeg && hasPos ? chartH / 2 : hasNeg ? 0 : chartH);
    const span = hasNeg && hasPos ? chartH / 2 : chartH;
    ctx.strokeStyle = COLORS.border;
    ctx.setLineDash([4, 6]);
    ctx.beginPath(); ctx.moveTo(pad, zero); ctx.lineTo(w - pad, zero); ctx.stroke();
    ctx.setLineDash([]);
    const slot = (w - pad * 2) / Math.max(stats.bars.length, 5);
    stats.bars.forEach((b, i) => {
      if (!b) return;
      const bh = Math.max(4, (Math.abs(b) / maxAbs) * (span - 4));
      const x = pad + i * slot + slot * 0.18;
      ctx.fillStyle = b > 0 ? COLORS.gain : COLORS.loss;
      roundRect(ctx, x, b > 0 ? zero - bh : zero, slot * 0.64, bh, Math.min(6, slot * 0.2));
      ctx.fill();
    });
  }

  drawFooter(ctx, w, h, pad);
}

// The card's background (dark navy, a soft glow and the site's grid) and the logo header.
export function drawBackdrop(ctx, w, h, pad) {
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, w, h);
  const glow = ctx.createRadialGradient(w * 0.8, h * 0.1, 0, w * 0.8, h * 0.1, w * 0.8);
  glow.addColorStop(0, 'rgba(10, 37, 70, 0.95)');
  glow.addColorStop(1, 'rgba(4, 13, 31, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = 'rgba(31, 215, 239, 0.07)';
  ctx.lineWidth = 1;
  for (let x = 0; x < w; x += 54) { ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, h); ctx.stroke(); }
  for (let y = 0; y < h; y += 54) { ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(w, y + 0.5); ctx.stroke(); }
  ctx.fillStyle = COLORS.cyan;
  ctx.fillRect(0, 0, w, 8);
  drawLogo(ctx, pad, pad, 64);
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = COLORS.text;
  ctx.font = '800 30px Inter, sans-serif';
  ctx.fillText('MARKET MATRIX', pad + 84, pad + 32);
  ctx.fillStyle = COLORS.cyan;
  ctx.font = '700 15px Inter, sans-serif';
  ctx.fillText(tn('TRADING JOURNAL'), pad + 85, pad + 56);
}

export function drawFooter(ctx, w, h, pad) {
  const site = getCurrentLang() === 'es' ? 'marketmatrixhq.com/es' : 'marketmatrixhq.com';
  ctx.font = '800 22px Inter, sans-serif';
  const room = w - pad * 2 - ctx.measureText(site).width - 24;
  // The tagline shrinks to keep clear of the address (Spanish is longer).
  let size = 20;
  const tagline = tn('Tracked with Market Matrix, the free journal for prop traders');
  ctx.font = `600 ${size}px Inter, sans-serif`;
  while (ctx.measureText(tagline).width > room && size > 13) { size -= 1; ctx.font = `600 ${size}px Inter, sans-serif`; }
  ctx.fillStyle = COLORS.muted;
  ctx.fillText(tagline, pad, h - pad + 18);
  ctx.textAlign = 'right';
  ctx.fillStyle = COLORS.cyan;
  ctx.font = '800 22px Inter, sans-serif';
  ctx.fillText(site, w - pad, h - pad + 18);
  ctx.textAlign = 'left';
}

function ShareCard({ trades, account, userName, user, onClose }) {
  useEffect(() => trackEvent('Share card'), []);
  const { t, lang } = useI18n();
  const [period, setPeriod] = useState('week');
  const [hideAmounts, setHideAmounts] = useState(false);
  const [showName, setShowName] = useState(false);
  const [showBadge, setShowBadge] = useState(true);
  const badge = user && user.isCreator ? { creator: true } : user && user.foundingNumber ? { number: user.foundingNumber } : null;
  const [sizeId, setSizeId] = useState('wide');
  const canvas = useRef(null);
  const dialog = useRef(null);
  const stats = useMemo(() => shareStats(trades, period, account), [trades, period, account]);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    dialog.current?.focus();
    function onKey(e) {
      if (e.key === 'Escape') closeRef.current();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Wait for the site's fonts so the card uses them.
    Promise.all([
      document.fonts.load('800 30px Inter'), document.fonts.load('600 20px Inter'), document.fonts.load('600 36px "JetBrains Mono"'),
    ]).catch(() => {}).then(() => {
      if (!cancelled && canvas.current) drawCard(canvas.current, {
        stats, period, hideAmounts, sizeId, name: showName ? userName : '', badge: showBadge ? badge : null,
      });
    });
    return () => { cancelled = true; };
  }, [stats, period, hideAmounts, sizeId, showName, userName, lang, showBadge, badge && badge.creator, badge && badge.number]); // eslint-disable-line react-hooks/exhaustive-deps

  const fileName = `market-matrix-${period}-${dayKey(new Date())}.png`;

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={dialog} tabIndex={-1} className="modal modal-wide share-modal" role="dialog" aria-modal="true" aria-labelledby="share-title">
        <div className="modal-header">
          <h2 id="share-title">{t('Share your results')}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        <div className="share-options">
          <div className="inbox-filter" role="group" aria-label={t('Period')}>
            {PERIODS.map((p) => (
              <button key={p.id} type="button" className={`secondary small${period === p.id ? ' active' : ''}`} onClick={() => setPeriod(p.id)}>{t(p.label)}</button>
            ))}
          </div>
          <div className="inbox-filter" role="group" aria-label={t('Image size')}>
            {Object.entries(SIZES).map(([id, s]) => (
              <button key={id} type="button" className={`secondary small${sizeId === id ? ' active' : ''}`} onClick={() => setSizeId(id)}>{t(s.label)}</button>
            ))}
          </div>
          <label className="share-toggle">
            <input type="checkbox" checked={hideAmounts} onChange={(e) => setHideAmounts(e.target.checked)} />
            {account && account.size ? t('Hide dollar amounts (shows % return instead)') : t('Hide dollar amounts')}
          </label>
          <label className="share-toggle">
            <input type="checkbox" checked={showName} onChange={(e) => setShowName(e.target.checked)} />
            {t('Show my name')}
          </label>
          {badge && (
            <label className="share-toggle">
              <input type="checkbox" checked={showBadge} onChange={(e) => setShowBadge(e.target.checked)} />
              {badge.creator ? t('Show my Founder badge') : t('Show my Founding Member badge')}
            </label>
          )}
        </div>
        <div className="share-preview">
          <canvas ref={canvas} aria-label={t('Preview of your share image')} />
        </div>
        {stats.count === 0 && <p className="field-hint">{t('No trades in this period yet, so the card shows zeros.')}</p>}
        <ImageActions canvas={canvas} fileName={fileName}
          shareText={t('My trading results, tracked with Market Matrix: {url}', { url: lang === 'es' ? 'https://marketmatrixhq.com/es' : 'https://marketmatrixhq.com' })} />
      </div>
    </div>
  );
}

// Download, copy and share buttons for a card drawn on a canvas.
export function ImageActions({ canvas, fileName, shareText }) {
  const { t } = useI18n();
  const [status, setStatus] = useState('');
  const toBlob = () => new Promise((resolve) => canvas.current.toBlob(resolve, 'image/png'));

  async function download() {
    const url = URL.createObjectURL(await toBlob());
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setStatus(t('Downloaded.'));
  }

  async function copy() {
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': await toBlob() })]);
      setStatus(t('Copied. Paste it into Discord or X.'));
    } catch {
      setStatus(t("Your browser can't copy images. Use Download instead."));
    }
  }

  async function share() {
    try {
      const file = new File([await toBlob()], fileName, { type: 'image/png' });
      await navigator.share({ files: [file], text: shareText });
    } catch {
      // Closing the share sheet is fine.
    }
  }

  const canCopy = typeof window !== 'undefined' && window.ClipboardItem && navigator.clipboard && navigator.clipboard.write;
  const canShare = typeof navigator !== 'undefined' && navigator.canShare
    && navigator.canShare({ files: [new File([''], 'x.png', { type: 'image/png' })] });
  return (
    <div className="modal-actions">
      <button type="button" onClick={download}>{t('Download image')}</button>
      {canCopy && <button type="button" className="secondary" onClick={copy}>{t('Copy image')}</button>}
      {canShare && <button type="button" className="secondary" onClick={share}>{t('Share…')}</button>}
      {status && <span className="save-status success">{status}</span>}
    </div>
  );
}

export default ShareCard;
